import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  CertificateMicroPublishService,
  CertificatePublishHistoryDto,
  CertificatePublishHistoryResponse,
  CertificateMicroPublishRequest,
  CertificateMicroPublishResult
} from '../../../services/certificate-micro-publish.service';
import { TranslatedBaseError } from '../TranslatedBaseError';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'micro-publish-modal',
  templateUrl: './micro-publish-modal.component.html',
  styleUrls: ['./micro-publish-modal.component.scss']
})
export class MicroPublishModalComponent extends TranslatedBaseError implements OnInit {
  @Input() certificateId: string;
  @Input() certificateName: string;
  @Output() close = new EventEmitter<void>();

  public publishHistory: CertificatePublishHistoryDto[] = [];
  public selectedPaths: Set<string> = new Set();
  public isLoading = false;
  public isPublishing = false;
  public publishResult: CertificateMicroPublishResult | null = null;
  public showResult = false;

  // Pagination
  public currentPage = 0;
  public pageSize = 20;
  public totalElements = 0;
  public totalPages = 0;
  public expandedBackupRows: Set<string> = new Set();

  // Expose Math to template
  public Math = Math;

  constructor(
    private microPublishService: CertificateMicroPublishService,
    toastr: ToastrService,
    translateService: TranslateService
  ) {
    super(toastr, translateService);
  }

  ngOnInit(): void {
    this.loadPublishHistory();
  }

  /**
   * Loads the publish history for the certificate
   */
  loadPublishHistory(): void {
    this.isLoading = true;
    this.microPublishService.getPublishHistory(this.certificateId, this.currentPage, this.pageSize).subscribe({
      next: (response: CertificatePublishHistoryResponse) => {
        this.publishHistory = response.content;
        this.totalElements = response.totalElements;
        this.totalPages = response.totalPages;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading publish history:', error);
        this.showErrorMessage('Failed to load publish history');
        this.isLoading = false;
      }
    });
  }

  /**
   * Toggles path selection
   */
  togglePath(path: string): void {
    if (this.selectedPaths.has(path)) {
      this.selectedPaths.delete(path);
    } else {
      this.selectedPaths.add(path);
    }
  }

  /**
   * Checks if a path is selected
   */
  isPathSelected(path: string): boolean {
    return this.selectedPaths.has(path);
  }

  /**
   * Selects all paths
   */
  selectAll(): void {
    this.publishHistory.forEach(entry => {
      this.selectedPaths.add(entry.path);
    });
  }

  /**
   * Clears all selected paths
   */
  clearSelection(): void {
    this.selectedPaths.clear();
  }

  /**
   * Executes the micro-publish operation
   */
  executeMicroPublish(): void {
    if (this.selectedPaths.size === 0) {
      this.showErrorMessage('Please select at least one path to publish');
      return;
    }

    this.isPublishing = true;
    this.showResult = false;

    const request: CertificateMicroPublishRequest = {
      selectedPaths: Array.from(this.selectedPaths)
    };

    this.microPublishService.executeMicroPublish(this.certificateId, request).subscribe({
      next: (result) => {
        this.publishResult = result;
        this.showResult = true;
        this.isPublishing = false;

        if (result.success) {
          this.responseSuccessful(
            `Successfully published ${result.successfulOverwrites} of ${result.totalPathsProcessed} paths`
          );
          // Reload history to show new micro-publish record
          this.loadPublishHistory();
          this.clearSelection();
        } else {
          this.showErrorMessage(
            result.errorMessage || 'Micro-publish completed with errors'
          );
        }
      },
      error: (error) => {
        console.error('Error executing micro-publish:', error);
        this.showErrorMessage('Failed to execute micro-publish operation');
        this.isPublishing = false;
      }
    });
  }

  /**
   * Closes the modal
   */
  closeModal(): void {
    this.close.emit();
  }

  /**
   * Formats a date for display
   */
  formatDate(date: Date | string): string {
    if (!date) {return '';}
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleString();
  }

  /**
   * Gets badge class for publish type
   */
  getPublishTypeBadge(type: string): string {
    return type === 'full' ? 'badge-primary' : 'badge-info';
  }

  /**
   * Gets badge class for overwrite status
   */
  getOverwriteStatusBadge(overwritten: boolean): string {
    return overwritten ? 'badge-warning' : 'badge-success';
  }

  /**
   * Gets overwrite status text
   */
  getOverwriteStatusText(overwritten: boolean): string {
    return overwritten ? 'Overwritten' : 'Original';
  }

  /**
   * Copies path to clipboard
   */
  copyPathToClipboard(path: string): void {
    navigator.clipboard.writeText(path).then(() => {
      this.responseSuccessful('Path copied to clipboard');
    }).catch((err) => {
      console.error('Could not copy path: ', err);
      this.showErrorMessage('Failed to copy path to clipboard');
    });
  }

  /**
   * Goes to the next page
   */
  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadPublishHistory();
    }
  }

  /**
   * Goes to the previous page
   */
  previousPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadPublishHistory();
    }
  }

  /**
   * Goes to a specific page
   */
  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.loadPublishHistory();
    }
  }

  /**
   * Gets array of page numbers to display
   */
  getPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(0, this.currentPage - Math.floor(maxPagesToShow / 2));
    const endPage = Math.min(this.totalPages - 1, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage < maxPagesToShow - 1) {
      startPage = Math.max(0, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  /**
   * Toggles backup info visibility for a row
   */
  toggleBackupInfo(path: string): void {
    if (this.expandedBackupRows.has(path)) {
      this.expandedBackupRows.delete(path);
    } else {
      this.expandedBackupRows.add(path);
    }
  }

  /**
   * Checks if backup info is expanded for a row
   */
  isBackupInfoExpanded(path: string): boolean {
    return this.expandedBackupRows.has(path);
  }

  /**
   * Gets display name for user (uses publishedByDisplayName if available, falls back to publishedBy)
   */
  getUserDisplayName(entry: CertificatePublishHistoryDto): string {
    return entry.publishedByDisplayName || entry.publishedBy;
  }
}
